// [批2 20260818] 波段页重构: 顶层[实盘][模拟]工作区 = 数据边界(Steven 六点 + Hermes IA)
//   实盘添加只出券商持仓选择器(无手输框, 后端 fail-closed 双保险); 模拟可选持仓也可手输
//   交易日志时间线 = swing_signal_events + swing_events 合并(Steven 追加确认项)
//   订单区 Phase1 = 信号卡状态流转(不虚构没有 producer 的券商订单区)
// 前版判例: 全局参数纵向铺满主页 + signals 查了不渲染 + 实盘可手输任意码
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Waves, RefreshCw, Plus, X, TrendingUp, Wallet, ListChecks, Settings2, CalendarRange, ScrollText } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

const CFG_CN: Record<string, { label: string; unit?: string }> = {
  risk_per_stock: { label: "实盘·单票风险额", unit: "元" },
  max_nominal_per_stock: { label: "实盘·单票名义上限", unit: "元" },
  max_total_nominal: { label: "实盘·总仓名义上限", unit: "元" },
  max_positions: { label: "实盘·最大持仓只数" },
  sim_risk: { label: "SIM·单票风险额", unit: "元" },
  sim_pos_cap: { label: "SIM·单票名义上限", unit: "元" },
  stop_atr: { label: "止损ATR倍数(两模式共用)" },
  tp_atr: { label: "首目标ATR倍数(两模式共用)" },
  time_stop_days: { label: "时间止损天数", unit: "天" },
  addon_enable: { label: "加仓臂开关(0关1开)" },
  addon_trigger_atr: { label: "加仓触发ATR" },
  addon_frac: { label: "加仓比例(首仓)" },
  min_daily_amount: { label: "流动性下限", unit: "元/日" },
  chase_max_pct: { label: "追价上限", unit: "%" },
  signal_valid_min: { label: "信号有效期", unit: "分" },
  real_cards_enabled: { label: "实盘交易卡总闸(0关1开)" },
  phase2_gate_signals: { label: "半自动升级门槛", unit: "信号" },
  config_ver: { label: "参数版本号(自动)" },
};
const CFG_ORDER = Object.keys(CFG_CN);
const SIG_CN: Record<string, string> = {
  pushed: "已发卡", filled: "已成交", partial: "部分成交", expired: "已过期",
  abandoned: "已放弃", unfillable: "不可成交", corrected: "偏离修正",
};
const EV_CN: Record<string, string> = {
  monitor_added: "加入监控", monitor_removed: "移除监控", mode_changed: "模式变更",
  signal_created: "产生信号", signal_rejected: "信号被拒", signal_expired: "信号过期",
  card_pushed: "推送交易卡", fill_reported: "回报成交", partial_fill_reported: "回报部分成交",
  position_opened: "建仓", position_adjusted: "调仓", position_closed: "平仓",
  stop_updated: "止损更新", param_changed: "参数变更", t1_blocked_would_exit: "T+1锁定(本应卖出)",
  reconcile_warning: "对账警告",
};

export default function SwingTrade() {
  const utils = trpc.useUtils();
  const [mode, setMode] = useState<"real" | "sim">("sim");
  const { data, isLoading, refetch } = trpc.swingTrade.state.useQuery(undefined, { refetchInterval: 30000 });
  const { data: monthly } = trpc.swingTrade.monthly.useQuery(undefined, { refetchInterval: 60000 });
  const { data: bh } = trpc.swingTrade.brokerHoldings.useQuery(undefined, { refetchInterval: 60000 });
  const positions = useMemo(() => (data?.positions ?? []).filter((p: any) => p.mode === mode), [data, mode]);
  const codes = useMemo(() => positions.map((p: any) => p.code), [positions]);
  const { data: quotes } = trpc.swingTrade.quotes.useQuery({ codes }, { enabled: codes.length > 0, refetchInterval: 30000 });
  const setCfg = trpc.swingTrade.setConfig.useMutation({
    onSuccess: () => { toast.success("已应用并审计"); utils.swingTrade.state.invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const addStock = trpc.swingTrade.addStock.useMutation({
    onSuccess: (r) => { toast.success(`已加入${mode === "real" ? "实盘" : "模拟"}监控: ${r.name}`); setNewCode(""); utils.swingTrade.state.invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const delStock = trpc.swingTrade.delStock.useMutation({ onSuccess: () => utils.swingTrade.state.invalidate() });
  // [SW3v2-UI 20260818] 划拨台账 hooks(B口径); 路由未部署时 optional-chain 安静降级
  const allocList = (trpc.swingTrade as any).allocList?.useQuery?.(undefined, { refetchInterval: 60000, enabled: mode === "real" });
  const allocTransfer = (trpc.swingTrade as any).allocTransfer?.useMutation?.({ onSuccess: () => allocList?.refetch?.() });
  const [allocEdits, setAllocEdits] = useState<Record<string, string>>({}); // [UX-MERGE 20260818] 行内划拨
  // [UX-J3 20260818] Web 成交登记(替代命令行 swing_fill)
  const [fillForm, setFillForm] = useState<null | { code: string; name?: string; action: "entry" | "exit" | "seed"; px: string; qty: string; reason: string }>(null);
  const [fillMsg, setFillMsg] = useState("");
  const [guardEdit, setGuardEdit] = useState<null | { code: string; name?: string; stop: string; tp: string }>(null); // [GUARD-ADJ 20260819]
  const adjustGuardM = (trpc.swingTrade as any).adjustGuard?.useMutation?.({
    onSuccess: () => { setGuardEdit(null); setFillMsg("守护已调整"); utils.swingTrade.state.invalidate(); },
    onError: (e: any) => setFillMsg(String(e?.message ?? "").slice(0, 160)),
  });
  const swingFillM = (trpc.swingTrade as any).swingFill?.useMutation?.({
    onSuccess: (d: any) => { setFillMsg(String(d?.output ?? "")); if (d?.ok) setFillForm(null); utils.swingTrade.state.invalidate(); allocList?.refetch?.(); },
    onError: (e: any) => setFillMsg(String(e?.message ?? "").slice(0, 200)),
  });
  const setStockParams = (trpc.swingTrade as any).setStockParams?.useMutation?.({
    onSuccess: () => { toast.success("单股参数已保存并审计"); setEditStock(null); utils.swingTrade.state.invalidate(); },
    onError: (e: any) => toast.error(e.message),
  });
  const [editStock, setEditStock] = useState<string | null>(null);
  const [stockEdits, setStockEdits] = useState<Record<string, string>>({});
  const PER_STOCK: { k: string; label: string }[] = [
    { k: "risk_per_stock", label: "单票风险额(元)" }, { k: "max_nominal_per_stock", label: "单票名义上限(元)" },
    { k: "stop_atr", label: "止损ATR" }, { k: "tp_atr", label: "首目标ATR" },
    { k: "time_stop_days", label: "时间止损(天)" }, { k: "chase_max_pct", label: "追价上限(%)" },
  ];
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [newCode, setNewCode] = useState("");
  const [showCfg, setShowCfg] = useState(false);

  const thisYm = new Date().toISOString().slice(0, 7);
  const pnlColor = (v: number) => (v >= 0 ? "text-red-500" : "text-green-600");
  const fmt = (v: number) => (v >= 0 ? "+" : "") + Math.round(v);
  const cfgMap = useMemo(() => Object.fromEntries((data?.config ?? []).map((c: any) => [c.k, c.v])), [data]);
  const holdings: any[] = bh?.holdings ?? [];
  const holdingCodes = useMemo(() => new Set(holdings.map((h) => h.code)), [holdings]);
  const manual = useMemo(() => (data?.manualStocks ?? []).filter((s: any) => (s.tradeMode === "real") === (mode === "real")), [data, mode]);
  const manualCodes = useMemo(() => new Set(manual.map((s: any) => s.code)), [manual]);
  const signals = useMemo(() => (data?.signals ?? []).filter((s: any) => s.mode === mode), [data, mode]);
  const events = useMemo(() => ((data as any)?.events ?? []).filter((e: any) => e.mode === mode), [data, mode]);
  const rounds = useMemo(() => (data?.rounds ?? []).filter((r: any) => r.mode === mode), [data, mode]);
  const stat = useMemo(() => (data?.stats ?? []).find((s: any) => s.mode === mode), [data, mode]);

  const floating = useMemo(() => {
    let f = 0;
    for (const p of positions) {
      const last = quotes?.[p.code]?.last ?? 0;
      if (last > 0) f += (last - Number(p.avgPx)) * Number(p.qty);
    }
    return f;
  }, [positions, quotes]);
  const monthRealized = useMemo(
    () => (monthly ?? []).filter((m: any) => m.ym === thisYm && m.mode === mode).reduce((s: number, m: any) => s + Number(m.pnl), 0),
    [monthly, thisYm, mode]);
  const monthTotal = monthRealized + floating;
  const pendingSignals = signals.filter((s: any) => s.status === "pushed").length;

  // 日志时间线: signals(带推送时间) + events 合并按时间倒序
  const timeline = useMemo(() => {
    const t: { ts: string; tag: string; text: string; warn?: boolean }[] = [];
    for (const s of signals) {
      t.push({ ts: String(s.sigDate), tag: SIG_CN[s.status] ?? s.status,
        text: `${s.name ?? s.code} ${s.sigType ?? ""} 确认价${s.confPx ?? "-"} 建议${s.suggestQty ?? "-"}股` +
          (s.fillPx ? ` → 成交${s.fillQty}股@${s.fillPx} 偏离${s.deviationPct ?? 0}%` : "") + (s.reason ? ` (${s.reason})` : ""),
        warn: ["abandoned", "corrected", "unfillable"].includes(String(s.status)) });
    }
    for (const e of events) {
      t.push({ ts: String(e.ts).replace("T", " ").slice(0, 16), tag: EV_CN[e.etype] ?? e.etype,
        text: `${e.code}${e.reason ? " " + e.reason : ""} (v${e.configVer ?? "-"}, ${e.source ?? "-"})`,
        warn: ["reconcile_warning", "t1_blocked_would_exit"].includes(String(e.etype)) });
    }
    return t.sort((a, b) => b.ts.localeCompare(a.ts)).slice(0, 60);
  }, [signals, events]);

  const doAdd = (code: string) => {
    if (mode === "real" && !window.confirm(`确认以【实盘】加入 ${code}?\n后端将核验券商真实持仓(不在持仓将被拒绝), 信号触发发实盘交易卡`)) return;
    addStock.mutate({ code, tradeMode: mode });
  };

  return (
    <div className="p-3 md:p-5 space-y-3 max-w-3xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Waves className="w-5 h-5 text-blue-500" />
          <h1 className="text-lg font-bold">波段交易</h1>
        </div>
        <Button size="sm" variant="ghost" onClick={() => refetch()}><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {/* 顶层工作区切换 = 数据边界: 下面所有区块只显示当前模式 */}
      <div className="grid grid-cols-2 rounded-lg border overflow-hidden">
        <button className={"py-2.5 text-sm font-semibold " + (mode === "real" ? "bg-red-500/15 text-red-500" : "opacity-60")}
          onClick={() => setMode("real")}>实盘{mode === "real" ? " ●" : ""}</button>
        <button className={"py-2.5 text-sm font-semibold " + (mode === "sim" ? "bg-blue-500/15 text-blue-500" : "opacity-60")}
          onClick={() => setMode("sim")}>模拟{mode === "sim" ? " ●" : ""}</button>
      </div>

      {/* 模式状态栏 */}
      <Card className={mode === "real" ? "bg-gradient-to-br from-red-500/10 to-transparent" : "bg-gradient-to-br from-blue-500/10 to-transparent"}>
        <CardContent className="py-4">
          <div className="flex items-end justify-between flex-wrap gap-2">
            <div>
              <div className="text-xs text-muted-foreground flex items-center gap-1"><Wallet className="w-3 h-3" />
                本月盈利(已实现{fmt(monthRealized)} + 浮动{fmt(floating)})</div>
              <div className={"text-3xl font-bold " + pnlColor(monthTotal)}>{fmt(monthTotal)}<span className="text-sm ml-1">元</span></div>
            </div>
            <div className="text-xs text-muted-foreground text-right space-y-0.5">
              <div>监控 {manual.length} · 持仓 {positions.length} · 待处理信号 {pendingSignals}</div>
              {stat && <div>累计 {stat.n}回合 · 胜率{stat.n > 0 ? Math.round((Number(stat.wins) / Number(stat.n)) * 100) : 0}% · <span className={pnlColor(Number(stat.totalPnl))}>{fmt(Number(stat.totalPnl))}</span></div>}
              {mode === "real" && cfgMap["real_cards_enabled"] !== "1" && <div className="text-orange-500 font-semibold">⚠ 实盘交易卡总闸=关(休眠中)</div>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* [UX-J3 20260818] 待你执行: 已推送未处理的实盘信号 */}
      {mode === "real" && signals.filter((s: any) => s.status === "pushed").length > 0 && (
        <Card className="border-orange-500/60">
          <CardHeader className="py-3"><CardTitle className="text-sm">⚡ 待你执行 ({pendingSignals})</CardTitle></CardHeader>
          <CardContent className="pt-0 space-y-2">
            {signals.filter((s: any) => s.status === "pushed").map((s: any) => (
              <div key={String(s.id ?? s.code) + String(s.sigType)} className="rounded-lg border px-3 py-2 text-xs flex flex-wrap items-center gap-2">
                <span className="font-semibold">{s.name ?? s.code} {s.code}</span>
                <span>{s.sigType} · 确认价 {s.confPx} · 建议 {s.suggestQty} 股</span>
                <span className="flex gap-1 ml-auto">
                  <Button size="sm" className="h-7 text-xs" onClick={() => { setFillMsg(""); setFillForm({ code: s.code, name: s.name, action: "entry", px: String(s.confPx ?? ""), qty: String(s.suggestQty ?? ""), reason: "" }); }}>已买入·登记</Button>
                  <Button size="sm" variant="secondary" className="h-7 text-xs" disabled={swingFillM?.isPending} onClick={() => { if (!window.confirm(`确认放弃 ${s.name ?? s.code} 的这条信号? 放弃后该信号作废, 不可恢复`)) return; setFillMsg(""); swingFillM?.mutate({ code: s.code, action: "abandon", reason: "manual" }); }}>放弃</Button>
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* [UX-J3 20260818] 成交登记表单 */}
      {fillForm && (
        <Card className="border-blue-500/60">
          <CardHeader className="py-3"><CardTitle className="text-sm">📝 成交登记: {fillForm.name ?? ""} {fillForm.code} · {fillForm.action === "entry" ? "买入" : fillForm.action === "seed" ? "存量守护登记" : "卖出"}</CardTitle></CardHeader>
          <CardContent className="pt-0 space-y-2 text-xs">
            <div className="flex flex-wrap items-center gap-2">
              <span>{fillForm.action === "seed" ? "基准价(成本价)" : "成交价"}</span><Input className="h-8 w-24" inputMode="decimal" value={fillForm.px} onChange={(e) => setFillForm({ ...fillForm, px: e.target.value.trim() })} />
              <span>股数</span><Input className="h-8 w-24" inputMode="numeric" value={fillForm.qty} onChange={(e) => setFillForm({ ...fillForm, qty: e.target.value.trim() })} />
              {fillForm.action === "exit" && (<><span>原因</span><Input className="h-8 w-28" value={fillForm.reason} onChange={(e) => setFillForm({ ...fillForm, reason: e.target.value.trim() })} /></>)}
              <Button size="sm" className="h-8" disabled={swingFillM?.isPending || !/^\d+(\.\d+)?$/.test(fillForm.px) || !/^\d+$/.test(fillForm.qty)}
                onClick={() => swingFillM?.mutate({ code: fillForm.code, action: fillForm.action, px: Number(fillForm.px), qty: Number(fillForm.qty), reason: fillForm.reason || undefined })}>提交登记</Button>
              <Button size="sm" variant="secondary" className="h-8" onClick={() => { setFillForm(null); setFillMsg(""); }}>取消</Button>
            </div>
            <div className="text-muted-foreground">在券商 App 成交后回来登记(系统不代下单)。价格须在当日高低区间内(后端校验); 卖出股数=全部即清仓, 改小=部分卖出。</div>
            {fillMsg && <div className={/fill ok|exit ok|abandoned|seed ok/.test(fillMsg) ? "text-green-600" : "text-red-500"}>{fillMsg}</div>}
          </CardContent>
        </Card>
      )}
      {fillMsg && !fillForm && <div className={"text-xs px-1 " + (/fill ok|exit ok|abandoned|seed ok/.test(fillMsg) ? "text-green-600" : "text-red-500")}>登记结果: {fillMsg} <button className="underline opacity-60" onClick={() => setFillMsg("")}>关闭</button></div>}

      {/* 持仓 */}
      <Card>
        <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-1"><TrendingUp className="w-4 h-4" />{mode === "real" ? "实盘" : "模拟"}持仓 ({positions.length}) {isLoading && "…"}</CardTitle></CardHeader>
        <CardContent className="space-y-2 pt-0">
          {positions.map((p: any) => {
            const qt = quotes?.[p.code];
            const last = qt?.last ?? 0;
            const upnl = last > 0 ? (last - p.avgPx) * p.qty : 0;
            const upct = last > 0 ? ((last - p.avgPx) / p.avgPx) * 100 : 0;
            const dStop = last > 0 && p.stopPx ? ((last - p.stopPx) / last) * 100 : null;
            return (
              <div key={p.mode + p.code} className="rounded-lg border p-3">
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{p.name} <span className="text-xs text-muted-foreground">{p.code} · v{p.configVer}</span></div>
                  <div className={"text-lg font-bold " + pnlColor(upnl)}>{last > 0 ? fmt(upnl) : "-"}<span className="text-xs ml-1">{last > 0 ? `(${upct >= 0 ? "+" : ""}${upct.toFixed(1)}%)` : ""}</span></div>
                </div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="whitespace-nowrap">持 {p.qty}股 @{Number(p.avgPx).toFixed(2)}</span>
                  <span>现价 {last > 0 ? last.toFixed(2) : "-"}</span>
                  <span>{String(p.entryDate).slice(5, 10)} 入</span>
                  <span className={"whitespace-nowrap " + (dStop != null && dStop < 2 ? "text-orange-500 font-semibold" : "")}>止损 {Number(p.stopPx).toFixed(2)}{dStop != null ? ` (距${dStop.toFixed(1)}%)` : ""}</span>
                  <span>目标 {Number(p.tp1Px).toFixed(2)}{last > 0 && p.tp1Px ? ` (距${(((Number(p.tp1Px) - last) / last) * 100).toFixed(1)}%)` : ""}</span>
                  <span>峰值 {Number(p.peakPx).toFixed(2)}</span>
                </div>
                {mode === "real" && (
                  <div className="mt-2 flex flex-wrap gap-1.5">{/* [UX-J3 20260818] 行内登记入口 */}
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setFillMsg(""); setFillForm({ code: p.code, name: p.name, action: "exit", px: last > 0 ? last.toFixed(2) : "", qty: String(Math.max(100, Math.floor(p.qty / 2 / 100) * 100)), reason: "tp1" }); }}>卖半·登记</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setFillMsg(""); setFillForm({ code: p.code, name: p.name, action: "exit", px: last > 0 ? last.toFixed(2) : "", qty: String(p.qty), reason: "manual_exit" }); }}>全卖·登记</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setFillMsg(""); setGuardEdit({ code: p.code, name: p.name, stop: Number(p.stopPx).toFixed(3), tp: Number(p.tp1Px).toFixed(3) }); }}>调止损/止盈</Button>
                  </div>
                )}
                {guardEdit && guardEdit.code === p.code && (
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs rounded-lg border border-blue-500/40 p-2">
                    <span>止损</span><Input className="h-7 w-24" inputMode="decimal" value={guardEdit.stop} onChange={(e) => setGuardEdit({ ...guardEdit, stop: e.target.value.trim() })} />
                    <span>止盈</span><Input className="h-7 w-24" inputMode="decimal" value={guardEdit.tp} onChange={(e) => setGuardEdit({ ...guardEdit, tp: e.target.value.trim() })} />
                    <Button size="sm" className="h-7 text-xs" disabled={adjustGuardM?.isPending || !/^\d+(\.\d+)?$/.test(guardEdit.stop) || !/^\d+(\.\d+)?$/.test(guardEdit.tp)}
                      onClick={() => adjustGuardM?.mutate({ code: p.code, stopPx: Number(guardEdit.stop), tp1Px: Number(guardEdit.tp) })}>保存并审计</Button>
                    <Button size="sm" variant="secondary" className="h-7 text-xs" onClick={() => setGuardEdit(null)}>取消</Button>
                    <span className="text-[10px] text-muted-foreground">生效于下一哨兵循环(≤2分钟); 止损≥现价会立即触发红卡, 请自查</span>
                  </div>
                )}
              </div>
            );
          })}
          {positions.length === 0 && <div className="text-sm text-muted-foreground">当前无{mode === "real" ? "实盘" : "模拟"}持仓</div>}
        </CardContent>
      </Card>

      {/* 监控与添加: 实盘=只出持仓选择器; 模拟=选择器+手输 */}
      <Card>
        <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-1"><ListChecks className="w-4 h-4" />{mode === "real" ? "实盘" : "模拟"}监控 ({manual.length})</CardTitle></CardHeader>
        <CardContent className="pt-0 space-y-3">
          {mode === "sim" ? (
          <div className="flex flex-wrap gap-1.5">
            {manual.map((s: any) => (
              <Badge key={s.code} variant="outline" className="pl-2 pr-1 py-1 text-xs gap-1">
                {s.name} {s.code}
                <button className={"rounded px-1 text-[10px] " + (s.paramJson ? "bg-blue-500/20 text-blue-500 font-semibold" : "opacity-60")}
                  title={s.paramJson ? "有单股覆盖" : "单股参数(一般无需设置)"}
                  onClick={() => { setEditStock(editStock === s.code ? null : s.code); setStockEdits(s.paramJson ? JSON.parse(s.paramJson) : {}); }}>参</button>
                <button className="ml-0.5 opacity-60 hover:opacity-100" onClick={() => delStock.mutate({ code: s.code, tradeMode: mode })}><X className="w-3 h-3" /></button>
              </Badge>
            ))}
            {manual.length === 0 && <span className="text-xs text-muted-foreground">暂无监控</span>}
          </div>
          ) : (
          <div className="space-y-1.5">{/* [UX-MERGE 20260818] 实盘行: 持仓/已划/行内划拨一屏完成 */}
            {manual.map((s: any) => {
              const bhQty = holdings.find((h) => h.code === s.code)?.qty ?? 0;
              const net = Number(((allocList?.data as any)?.nets ?? []).find((n: any) => n.code === s.code)?.net ?? 0);
              return (
                <div key={s.code} className="rounded-lg border px-3 py-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                  <span className="font-semibold whitespace-nowrap">{s.name} {s.code}</span>
                  <span className="text-muted-foreground whitespace-nowrap">持仓 {bhQty || "?"}</span>
                  <span className={"whitespace-nowrap " + (net > 0 ? "text-blue-500 font-semibold" : "text-muted-foreground")}>波段 {net} 股</span>
                  <span className="flex items-center gap-1 ml-auto">
                    <Input className="h-7 w-24 text-xs" placeholder="±股数" inputMode="numeric" value={allocEdits[s.code] ?? ""}
                      onChange={(e) => setAllocEdits((o) => ({ ...o, [s.code]: e.target.value.trim() }))} />
                    <Button size="sm" className="h-7 text-xs px-2" disabled={!/^-?\d+$/.test(allocEdits[s.code] ?? "") || Number(allocEdits[s.code]) === 0 || !allocTransfer || allocTransfer.isPending}
                      onClick={() => { allocTransfer?.mutate({ code: s.code, deltaQty: Number(allocEdits[s.code]), note: "web划拨(行内)" }); setAllocEdits((o) => ({ ...o, [s.code]: "" })); }}>划拨</Button>
                    {net > 0 && !positions.some((p: any) => p.code === s.code) && (
                      <Button size="sm" variant="outline" className="h-7 text-xs px-2 text-blue-500" title="已划拨但哨兵还没盯: 登记后开始盯止损/目标"
                        onClick={() => { setFillMsg(""); setFillForm({ code: s.code, name: s.name, action: "seed", px: "", qty: String(net), reason: "" }); }}>登记守护</Button>
                    )}
                    <button className={"rounded px-1.5 py-0.5 " + (s.paramJson ? "bg-blue-500/20 text-blue-500 font-semibold" : "border opacity-70")}
                      title={s.paramJson ? "有单股覆盖" : "单股参数(一般无需设置)"}
                      onClick={() => { setEditStock(editStock === s.code ? null : s.code); setStockEdits(s.paramJson ? JSON.parse(s.paramJson) : {}); }}>参</button>
                    <button className="opacity-60 hover:opacity-100" onClick={() => delStock.mutate({ code: s.code, tradeMode: mode })}><X className="w-3 h-3" /></button>
                  </span>
                </div>
              );
            })}
            {manual.length === 0 && <span className="text-xs text-muted-foreground">暂无监控 — 从下方券商持仓点 + 添加</span>}
            {manual.length > 0 && <div className="text-[10px] text-muted-foreground">划拨: 正数=划入波段(归波段守护/可卖, T0 从此碰不了), 负数=划回; 上限=券商可卖量, 仅盘外可操作, 划错填负数即可撤回</div>}
            {allocTransfer?.error && <div className="text-xs text-red-500">{String((allocTransfer.error as any)?.message ?? "").slice(0, 140)}</div>}
          </div>
          )}
          {editStock && (
            <div className="rounded-lg border p-3 space-y-2 bg-blue-500/5">
              <div className="text-xs font-semibold">单股参数覆盖: {editStock}</div>
              <div className="text-[11px] text-muted-foreground">一般<b>无需设置</b>: 全部留空 = 继承全局参数(全局值已过回测判决, 是推荐值)。只有想让个别票更保守(调小仓位/收紧止损)或更激进时才填对应格子, 生效于下一次信号/哨兵循环。</div>
              {PER_STOCK.map(({ k, label }) => (
                <div key={k} className="flex items-center gap-2">
                  <span className="text-xs w-32 shrink-0">{label}</span>
                  <Input className="h-8 flex-1 text-sm" placeholder={`全局 ${cfgMap[k] ?? "-"}`}
                    value={stockEdits[k] ?? ""}
                    onChange={(e) => setStockEdits((o) => ({ ...o, [k]: e.target.value.trim() }))} />
                  <span className="text-[10px] text-muted-foreground w-14 shrink-0">{(stockEdits[k] ?? "") !== "" ? "覆盖" : "全局"}</span>
                </div>
              ))}
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="secondary" onClick={() => setEditStock(null)}>取消</Button>
                <Button size="sm" disabled={!setStockParams || setStockParams.isPending}
                  onClick={() => setStockParams?.mutate({ code: editStock, tradeMode: mode, params: stockEdits })}>保存并审计</Button>
              </div>
            </div>
          )}
          <div>
            <div className="text-xs text-muted-foreground mb-1">
              {mode === "real"
                ? (bh?.ok ? "从券商真实持仓添加(实盘只能选持仓; 后端二次核验)" : `⚠ 持仓核验不可达: ${bh?.reason ?? "…"} — 实盘添加已锁定(fail-closed)`)
                : "从持仓快捷添加, 或手输任意6位代码"}
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {holdings.map((h) => (
                <Button key={h.code} size="sm" variant="outline" className="h-8 text-xs" disabled={manualCodes.has(h.code) || addStock.isPending}
                  onClick={() => doAdd(h.code)}>
                  <Plus className="w-3 h-3 mr-0.5" />{h.name || h.code} ({h.qty}股)
                </Button>
              ))}
              {mode === "real" && bh?.ok && holdings.length === 0 && <span className="text-xs text-muted-foreground">券商账户当前无持仓</span>}
            </div>
            {mode === "sim" && (
              <div className="flex gap-2">
                <Input className="h-9 flex-1 min-w-[8rem]" placeholder="6位代码, 如 600519" value={newCode} inputMode="numeric"
                  onChange={(e) => setNewCode(e.target.value.trim())} />
                <Button size="sm" className="h-9" disabled={!/^\d{6}$/.test(newCode) || addStock.isPending}
                  onClick={() => doAdd(newCode)}><Plus className="w-4 h-4 mr-1" />加入模拟</Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* [SW3v2-UI 20260818] 波段划拨台账: 划入的底仓归波段可卖, 其余份额受柜台门禁保护 */}
      {mode === "real" && (
        <Card>
          <CardHeader className="py-3"><CardTitle className="text-sm">波段划拨台账 <span className="font-normal text-muted-foreground">(划入=归波段可卖; T0 不可碰占用份额)</span></CardTitle></CardHeader>
          <CardContent className="pt-0 space-y-2">
            <div className="flex flex-wrap gap-1.5">
              {((allocList?.data as any)?.nets ?? []).map((n: any) => (
                <Badge key={n.code} variant="outline" className="text-xs">{n.code} 占用 {n.net} 股</Badge>
              ))}
              {((allocList?.data as any)?.nets ?? []).length === 0 && <span className="text-xs text-muted-foreground">暂无划拨 — 波段仅可卖自己买入并登记的部分</span>}
            </div>
            <div className="text-[10px] text-muted-foreground">划拨操作已合并到上方「实盘监控」每行(UX-MERGE 20260818); 本卡只展示占用汇总与最近流水。</div>
            <div className="text-[10px] text-muted-foreground space-y-0.5">
              {((allocList?.data as any)?.tail ?? []).slice(0, 8).map((t: any) => (
                <div key={t.id}>#{t.id} {String(t.createdAt).slice(5, 16)} {t.code} {t.deltaQty > 0 ? "+" : ""}{t.deltaQty} {t.reason} {t.note ?? ""}</div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 推荐池: 一键加入当前模式; 实盘下非持仓票只可加模拟 */}
      <Card>
        <CardHeader className="py-3"><CardTitle className="text-sm">推荐股票池</CardTitle></CardHeader>
        <CardContent className="pt-0 space-y-3">
          <div>
            <div className="text-xs text-muted-foreground mb-1">今日计划池(15:12生成)</div>
            <div className="flex flex-wrap gap-1.5">
              {(data?.simPlan?.plans ?? []).map((p: any) => {
                const blocked = mode === "real" && !holdingCodes.has(p.code);
                return (
                  <Button key={p.code} size="sm" variant={String(p.mode).startsWith("enter") ? "default" : "secondary"} className="h-8 text-xs"
                    title={blocked ? "非持仓票, 实盘不可加(可切到模拟加入)" : p.sig} disabled={blocked || manualCodes.has(p.code) || addStock.isPending}
                    onClick={() => doAdd(p.code)}>
                    {p.name} {String(p.mode).startsWith("enter") ? "▶" : p.mode === "prep" ? "备" : "观"}{blocked ? " ·仅可加模拟" : ""}
                  </Button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">全市场扫描 ready(15:25更新)</div>
            <div className="flex flex-wrap gap-1.5">
              {(data?.scanReady ?? []).map((s: any) => {
                const blocked = mode === "real" && !holdingCodes.has(s.code);
                return (
                  <Button key={s.code} size="sm" variant="outline" className="h-8 text-xs" title={blocked ? "非持仓票, 实盘不可加" : s.sig}
                    disabled={blocked || manualCodes.has(s.code) || addStock.isPending} onClick={() => doAdd(s.code)}>
                    {s.name} {s.code}{blocked ? " ·仅可加模拟" : ""}
                  </Button>
                );
              })}
              {(data?.scanReady ?? []).length === 0 && <span className="text-xs text-muted-foreground">暂无</span>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 交易日志时间线(Steven 追加确认项: 不能以历史回合代替) [UX-J3 20260818 低频折叠] */}
      <details>
      <summary className="text-xs text-muted-foreground cursor-pointer py-1 select-none">📜 交易日志 ({timeline.length}) — 点开查看</summary>
      <Card>
        <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-1"><ScrollText className="w-4 h-4" />交易日志 ({timeline.length})</CardTitle></CardHeader>
        <CardContent className="pt-0 space-y-1.5">
          {timeline.map((t, i) => (
            <div key={i} className={"flex items-start gap-2 rounded-md px-3 py-1.5 text-xs " + (t.warn ? "bg-orange-500/10" : "bg-muted/40")}>
              <span className="text-muted-foreground shrink-0 w-24">{t.ts.slice(5)}</span>
              <Badge variant={t.warn ? "destructive" : "secondary"} className="text-[10px] shrink-0">{t.tag}</Badge>
              <span className="min-w-0">{t.text}</span>
            </div>
          ))}
          {timeline.length === 0 && <div className="text-sm text-muted-foreground">该模式暂无日志(信号/事件产生后逐条落此)</div>}
        </CardContent>
      </Card>
      </details>

      {/* 历史回合 + 月度(分模式) */}
      <Card>
        <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-1"><CalendarRange className="w-4 h-4" />{mode === "real" ? "实盘" : "模拟"}回合与月度</CardTitle></CardHeader>
        <CardContent className="pt-0 space-y-2">
          <div className="space-y-1">
            {(monthly ?? []).filter((m: any) => m.mode === mode).map((m: any) => (
              <div key={m.ym} className={"flex items-center justify-between rounded-md px-3 py-1.5 text-sm " + (m.ym === thisYm ? "bg-blue-500/10 font-semibold" : "bg-muted/40")}>
                <span>{m.ym}</span>
                <span className="text-xs text-muted-foreground">{m.n}回合 · 胜{m.wins}</span>
                <span className={pnlColor(Number(m.pnl))}>{fmt(Number(m.pnl))}元</span>
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            {rounds.map((r: any, i: number) => (
              <div key={i} className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2 text-sm">
                <div>
                  <div className="font-medium">{r.name} <span className="text-xs text-muted-foreground">{r.qty}股</span></div>
                  <div className="text-xs text-muted-foreground">{String(r.entryDate).slice(5, 10)} {Number(r.entryPx).toFixed(2)} → {String(r.exitDate).slice(5, 10)} {Number(r.exitPx).toFixed(2)} · {r.exitReason}</div>
                </div>
                <div className={"font-semibold " + pnlColor(Number(r.pnl))}>{fmt(Number(r.pnl))}</div>
              </div>
            ))}
            {rounds.length === 0 && <div className="text-sm text-muted-foreground">暂无回合</div>}
          </div>
        </CardContent>
      </Card>

      {/* 高级设置(折叠): 全局参数不再铺主页; 单股覆盖待 cfg(code) 解析器落地后开放 */}
      <Card>
        <CardHeader className="py-3 cursor-pointer" onClick={() => setShowCfg(!showCfg)}>
          <CardTitle className="text-sm flex items-center gap-1"><Settings2 className="w-4 h-4" />高级设置(全局参数) · v{cfgMap["config_ver"]} {showCfg ? "▲" : "▼"}</CardTitle>
        </CardHeader>
        {showCfg && (
          <CardContent className="pt-0 space-y-3">
            <div className="text-xs text-muted-foreground">全局默认参数, 修改即审计并 config_ver+1。单股覆盖开放前, 所有票用全局值(来源: swing_config)。</div>
            {CFG_ORDER.filter((k) => k !== "config_ver" && cfgMap[k] !== undefined).map((k) => {
              const bigNum = Number(cfgMap[k]);
              const human = Number.isFinite(bigNum) && bigNum >= 10000
                ? (bigNum >= 100000000 ? (bigNum / 100000000).toFixed(1) + "亿" : (bigNum / 10000).toFixed(0) + "万") : null;
              return (
                <div key={k} className="rounded-lg border p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium">{CFG_CN[k].label}{CFG_CN[k].unit && <span className="text-xs text-muted-foreground ml-1">({CFG_CN[k].unit})</span>}</span>
                    <span className="text-xs text-muted-foreground">当前 {cfgMap[k]}{human ? ` (${human})` : ""}</span>
                  </div>
                  <div className="flex gap-2">
                    <Input className="h-10 flex-1 text-base" defaultValue={cfgMap[k]} inputMode="decimal"
                      onChange={(e) => setEdits((s) => ({ ...s, [k]: e.target.value.trim() }))} />
                    <Button className="h-10 px-4 shrink-0" variant={edits[k] !== undefined && edits[k] !== cfgMap[k] ? "default" : "secondary"}
                      disabled={edits[k] === undefined || edits[k] === cfgMap[k] || setCfg.isPending}
                      onClick={() => setCfg.mutate({ k, v: edits[k] })}>应用</Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        )}
      </Card>
    </div>
  );
}
